import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import FormulaRiskMatrix from "./FormulaRiskMatrix";
import structuredIfraData from "../data/structured_ifra_data.reconciled.json";
import {
  buildParsedResponseFromFormulaText,
  performFuzzySearchAndCheck,
} from "../utils/fuzzySearchUtils";
import { processApiResponse } from "../utils/openaiUtils";
import { buildProductCompliance } from "../platform/compliance";
import {
  buildFormulaRiskReport,
  downloadTextFile,
  serializeFormulaRiskReportJson,
  serializeFormulaRiskReportMarkdown,
} from "../platform/riskReport";
import { usePlatform } from "../platform/store";
import { Link as RouterLink } from "react-router-dom";
import { PageRoot, SourceCitation, StatusChip } from "./ui/PlatformPrimitives";

// Product-type presets — drive category-specific compliance evaluation
// (Annex IV/V/VI positive-list checks, sunscreen Drug Facts, AHA / hair-dye
// warnings, CLP hazard pictograms). Defaults to "fine-fragrance" for
// backward-compatibility with the original demo workflow.
const PRODUCT_TYPE_PRESETS = [
  { id: "fine-fragrance", label: "Fine Fragrance / EDP / EDC", category: "Fine Fragrance", fn: "Eau de Parfum" },
  { id: "skincare", label: "Skincare (cream / serum / lotion)", category: "Skincare", fn: "Face Serum" },
  { id: "sun-care", label: "Sun Care (SPF / UV protection)", category: "Sun Care", fn: "Daily Sunscreen" },
  { id: "hair-care", label: "Hair Care (shampoo / conditioner)", category: "Hair Care", fn: "Daily Cleansing Shampoo" },
  { id: "hair-dye", label: "Hair Colour / Dye", category: "Hair Care", fn: "Oxidative Hair Dye" },
  { id: "makeup", label: "Makeup (decorative cosmetic)", category: "Makeup", fn: "Colour Cosmetic" },
  { id: "rinse-off", label: "Other rinse-off cosmetic", category: "Personal Care", fn: "Cleanser" },
  { id: "other", label: "Other leave-on cosmetic", category: "Personal Care", fn: "Cosmetic" },
];

// Heuristic auto-detect — pick the most-specific matching preset based on
// the pasted formula's ingredients, so the user gets the right category
// pre-selected for them. Falls back to fine-fragrance.
const guessProductType = (text = "") => {
  const t = text.toLowerCase();
  if (/\b(octocrylene|avobenzone|ethylhexyl methoxycinnamate|butyl methoxydibenzoylmethane|titanium dioxide|zinc oxide|spf\s*\d|broad[-\s]?spectrum)/i.test(t)) return "sun-care";
  if (/\b(p[-\s]?phenylenediamine|toluene[-\s]?2,5[-\s]?diamine|resorcinol|persulfate|hydrogen peroxide)\b/i.test(t)) return "hair-dye";
  if (/\b(sodium laureth sulfate|sles|cocamidopropyl betaine|polyquaternium|panthenol)\b/i.test(t)) return "hair-care";
  if (/\b(niacinamide|sodium hyaluronate|hyaluronic|retinol|retinyl|ascorbic acid|salicylic|alpha[-\s]?hydroxy|glycolic)\b/i.test(t)) return "skincare";
  if (/\b(alcohol denat\.?|ethanol|sd\s*alcohol)/i.test(t) && !/\b(sodium laureth|cetearyl|niacinamide)\b/i.test(t)) return "fine-fragrance";
  return "fine-fragrance";
};

const TextPhotoProcessor = () => {
  const { products, regulatoryRules, regulatoryIngredients, createFormulaRun } = usePlatform();
  const [inputText, setInputText] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [results, setResults] = useState([]);
  const [parsedFormula, setParsedFormula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formulaName, setFormulaName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastRunMode, setLastRunMode] = useState("local");
  const [backendModel, setBackendModel] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("new");
  const [saved, setSaved] = useState(false);
  const [savedProductId, setSavedProductId] = useState("");
  const [savedProductName, setSavedProductName] = useState("");
  // User-selectable product type — if left on "auto" the heuristic picks
  // a sensible default from the pasted text. Drives category-specific
  // compliance: Annex VI UV filter limits for sunscreen, Annex III §11
  // for hair dye, AHA warnings, CLP hazard pictograms, etc.
  const [productType, setProductType] = useState("auto");
  const [spfClaim, setSpfClaim] = useState("");

  const verified = useMemo(() => results.filter((r) => r.validIngredient).length, [results]);
  const flagged = useMemo(() => results.filter((r) => r.above_threshold || !r.validIngredient).length, [results]);

  // Resolve the active product-type preset (auto-detect when set to "auto").
  const activePreset = useMemo(() => {
    const id = productType === "auto" ? guessProductType(inputText) : productType;
    return PRODUCT_TYPE_PRESETS.find((p) => p.id === id) || PRODUCT_TYPE_PRESETS[0];
  }, [productType, inputText]);

  const compliance = useMemo(() => {
    if (!parsedFormula || results.length === 0) return null;
    const claims = spfClaim.trim() ? [spfClaim.trim()] : [];
    return buildProductCompliance(
      {
        id: "preview",
        name: formulaName || "Preview",
        brand: "Preview",
        category: activePreset.category,
        function: activePreset.fn,
        markets: ["EU", "US"],
        responsiblePerson: "Preview", responsibleAddress: "", countryOfOrigin: "",
        netContent: "", packaging: {}, claims, requiredDocuments: [],
        formulaVersions: [{ id: "pf", name: formulaName || "Preview", source: lastRunMode, inputText, createdAt: new Date().toISOString(), parsedResponse: parsedFormula, ifraResults: results }],
        currentFormulaId: "pf",
      },
      [],
      { regulatoryIngredients: regulatoryIngredients || [], regulatoryChanges: [], regulatorySources: [], regulatoryRules: regulatoryRules || [] }
    );
  }, [parsedFormula, results, formulaName, lastRunMode, inputText, regulatoryIngredients, regulatoryRules, activePreset, spfClaim]);

  const applyParsed = (parsed, mode, model = "") => {
    setParsedFormula(parsed);
    setResults(performFuzzySearchAndCheck(parsed, structuredIfraData));
    setLastRunMode(mode);
    setBackendModel(model);
    setSaved(false);
    setSavedProductId("");
    setSavedProductName("");
  };

  const runLocal = async () => {
    if (!inputText.trim()) { setErrorMessage("Paste a formula."); return; }
    setLoading(true); setErrorMessage("");
    try { applyParsed(buildParsedResponseFromFormulaText(inputText, structuredIfraData), "local"); }
    catch (e) { setErrorMessage(e.message || "Unable to process."); }
    finally { setLoading(false); }
  };

  const runAi = async () => {
    if (!inputText.trim() && !imageBase64) { setErrorMessage("Paste a formula or upload an image."); return; }
    setLoading(true); setErrorMessage("");
    try {
      const r = await processApiResponse({ inputText, imageBase64 });
      applyParsed(r.parsedResponse, "openai", r.model || "");
    } catch (e) { setErrorMessage(e?.response?.data?.error || e.message || "Unable to process."); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!parsedFormula) return;
    try {
      const result = await createFormulaRun({
        productId: selectedProductId === "new" ? "" : selectedProductId,
        name: formulaName, inputText, source: lastRunMode,
        parsedResponse: parsedFormula, ifraResults: results, model: backendModel,
      });
      setSaved(true);
      if (selectedProductId !== "new") {
        const existing = products.find((p) => p.id === selectedProductId);
        setSavedProductId(selectedProductId);
        setSavedProductName(existing?.name || formulaName || "Product");
      } else {
        const newest = result?.products?.[0];
        setSavedProductId(newest?.id || "");
        setSavedProductName(newest?.name || formulaName || "New Product");
      }
    } catch (e) { setErrorMessage(e.message || "Unable to save."); }
  };

  const handleClear = () => {
    setInputText("");
    setImageBase64("");
    setUploadedFileName("");
    setResults([]);
    setParsedFormula(null);
    setFormulaName("");
    setErrorMessage("");
    setSelectedProductId("new");
    setSaved(false);
    setSavedProductId("");
    setSavedProductName("");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      const r = typeof reader.result === "string" ? reader.result : "";
      setImageBase64(r.includes(",") ? r.split(",")[1] : r);
    };
    reader.readAsDataURL(file);
  };

  const hasResults = results.length > 0;
  const euIssues = compliance?.jurisdictions?.EU?.issues || [];
  const usIssues = compliance?.jurisdictions?.US?.issues || [];
  const allIssues = [...euIssues, ...usIssues];
  const blockingIssues = allIssues.filter((i) => i.blocking);
  const allergenIssues = euIssues.filter((i) => i.code === "EU_LABEL_ALLERGEN_REVIEW");
  const nonAllergenIssues = allIssues.filter((i) => i.code !== "EU_LABEL_ALLERGEN_REVIEW");
  const riskReport = useMemo(() => {
    if (!compliance) return null;
    return buildFormulaRiskReport({
      product: {
        id: "preview",
        name: formulaName || "Preview",
        brand: "Preview",
        category: "Fine Fragrance",
        function: "Fragrance",
        markets: ["EU", "US"],
      },
      compliance,
      inputText,
    });
  }, [compliance, formulaName, inputText]);

  const reportFilenameBase = (formulaName || "formula-risk-report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const downloadReportJson = () => {
    if (!riskReport) return;
    downloadTextFile({
      filename: `${reportFilenameBase || "formula-risk-report"}.json`,
      content: serializeFormulaRiskReportJson(riskReport),
      type: "application/json",
    });
  };

  const downloadReportMarkdown = () => {
    if (!riskReport) return;
    downloadTextFile({
      filename: `${reportFilenameBase || "formula-risk-report"}.md`,
      content: serializeFormulaRiskReportMarkdown(riskReport),
      type: "text/markdown",
    });
  };

  return (
    <PageRoot>
      {/* Input area — contains name, product target, save, textarea.
          Paper auto-renders as theme glass surface (radius 16, surface tier);
          inner Stack is wrapped above the ::before so all interactive
          controls receive clicks. */}
      <Paper sx={{ p: { xs: 2.5, md: 3 }, animation: "fadeIn 300ms ease" }}>
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          {/* Row 1: name + product type + (SPF) + target + actions.
              Grid-based so columns reflow predictably at every breakpoint
              instead of a flex-wrap row whose spacer collapses on wrap. */}
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              alignItems: "start",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(12, minmax(0, 1fr))",
              },
            }}
          >
            <TextField
              size="small"
              placeholder="Formula name"
              value={formulaName}
              onChange={(e) => setFormulaName(e.target.value)}
              sx={{ gridColumn: { md: "span 3" } }}
            />
            <TextField
              size="small"
              select
              label="Product type"
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              sx={{ gridColumn: { md: "span 4" } }}
              helperText={
                productType === "auto"
                  ? `Auto: ${activePreset.label}`
                  : "Drives Annex IV/V/VI + sunscreen + hair-dye + AHA / CLP-flame checks"
              }
            >
              <MenuItem value="auto">Auto-detect from formula</MenuItem>
              {PRODUCT_TYPE_PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
              ))}
            </TextField>
            {(activePreset.id === "sun-care" || /\bspf/i.test(spfClaim)) && (
              <TextField
                size="small"
                placeholder="SPF claim e.g. SPF 30"
                value={spfClaim}
                onChange={(e) => setSpfClaim(e.target.value)}
                sx={{ gridColumn: { md: "span 2" } }}
              />
            )}
            <TextField
              size="small" select value={selectedProductId}
              onChange={(e) => { setSelectedProductId(e.target.value); setSaved(false); }}
              sx={{
                gridColumn: {
                  md: (activePreset.id === "sun-care" || /\bspf/i.test(spfClaim)) ? "span 3" : "span 5",
                },
              }}
            >
              <MenuItem value="new">New product</MenuItem>
              {products.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                gridColumn: { xs: "1 / -1" },
                justifySelf: { md: "end" },
                flexWrap: "wrap",
              }}
            >
              {saved ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ animation: "fadeIn 200ms ease" }}>
                  {/* Allow-listed severity contrast token (#2D6A4F). */}
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: "#2D6A4F" }} />
                  {savedProductId ? (
                    <Typography
                      variant="body2"
                      component={RouterLink}
                      to={`/perfume-dashboard?product=${savedProductId}`}
                      sx={{ color: "#2D6A4F", fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      Saved — view {savedProductName}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#2D6A4F", fontWeight: 500 }}>Saved</Typography>
                  )}
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={handleSave}
                  disabled={!parsedFormula}
                >
                  Save
                </Button>
              )}
              {hasResults && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClear}
                  startIcon={<ClearRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{ minWidth: 0 }}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Box>

          {/* Row 2: textarea */}
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={18}
            placeholder={"Paste your formula here...\n\nEthanol 65.00%\nLinalool 4.50%\nLimonene 3.20%\nCoumarin 1.50%"}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            sx={{
              // Defer to the theme MuiOutlinedInput glass-inset override
              // instead of forcing a solid grey fill — the textarea then
              // composes with its glass parent.
              "& .MuiOutlinedInput-root": {
                fontFamily: '"JetBrains Mono", "SF Mono", monospace',
                // 13px footnote — matches theme.typography.subtitle2.
                fontSize: "0.8125rem",
                lineHeight: 1.7,
              },
            }}
          />

          {/* Row 3: actions */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              size="small"
              startIcon={loading ? <CircularProgress size={14} sx={{ color: "common.white" }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={runLocal}
              disabled={loading}
              sx={{ minWidth: 90 }}
            >
              {loading ? "Running" : "Check"}
            </Button>
            <Button variant="outlined" size="small" onClick={runAi} disabled={loading} sx={{ minWidth: 60 }}>
              AI
            </Button>
            <Button component="label" variant="outlined" size="small" sx={{ minWidth: 36, px: 0.75 }}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
              <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
            </Button>
            {uploadedFileName && (
              <Typography variant="caption" color="text.secondary">{uploadedFileName}</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {hasResults && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />} onClick={downloadReportMarkdown} disabled={!riskReport}>
                  MD
                </Button>
                <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />} onClick={downloadReportJson} disabled={!riskReport}>
                  JSON
                </Button>
              </Stack>
            )}
          </Stack>

          {errorMessage && (
            // Allow-listed severity contrast token (#991B1B).
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "error.main" }}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="inherit">{errorMessage}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Results */}
      <Collapse in={hasResults} timeout={500}>
        <Box sx={{ display: "grid", gap: { xs: 2.5, md: 3 } }}>

          {/* Score strip — primary panel, theme glass SURFACE tier (16px radius).
              No manual dividers: items become independent pills that flex-wrap
              cleanly between mobile and desktop. */}
          <Paper
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 1.5, md: 2 },
              display: "flex",
              alignItems: "center",
              columnGap: { xs: 2, md: 3 },
              rowGap: 1.5,
              flexWrap: "wrap",
              animation: "fadeInUp 400ms ease both",
              position: "relative",
              "& > *": { position: "relative", zIndex: 1 },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="h3" color="text.primary" sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                {results.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">ingredients</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: "#2D6A4F" }} />
              <Typography variant="body2" sx={{ color: "#2D6A4F", fontWeight: 600 }}>{verified} verified</Typography>
            </Stack>
            {flagged > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#92400E" }} />
                <Typography variant="body2" sx={{ color: "#92400E", fontWeight: 600 }}>{flagged} flagged</Typography>
              </Stack>
            )}
            {blockingIssues.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "#991B1B" }} />
                <Typography variant="body2" sx={{ color: "#991B1B", fontWeight: 600 }}>{blockingIssues.length} blocking</Typography>
              </Stack>
            )}
            {/* Right-aligned status cluster — sits to the end of whatever
                row it lands on after wrapping. */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ ml: { md: "auto" } }}
            >
              {riskReport && <StatusChip status={riskReport.summary.overallStatus} />}
              {/* Navy brand-mark accent chip — translucent inset surface
                  (defers to theme MuiChip) with allow-listed #1B2A4A text. */}
              <Chip
                label={lastRunMode === "openai" ? `AI · ${backendModel || "OpenAI"}` : "Local"}
                size="small"
                sx={{
                  bgcolor: "rgba(27,42,74,0.10)",
                  color: "#1B2A4A",
                  borderRadius: 999,
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Paper>

          {/* Compliance issues — theme glass SURFACE tier (16px radius). */}
          {nonAllergenIssues.length + allergenIssues.length > 0 && (
            <Paper sx={{ overflow: "hidden", animation: "fadeInUp 500ms ease both" }}>
              <Box
                sx={{
                  px: { xs: 2.5, md: 3 },
                  py: 2,
                  // Hairline translucent divider (rgba 0.06).
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  background: "rgba(255,255,255,0.20)",
                  backdropFilter: "blur(8px) saturate(160%)",
                  WebkitBackdropFilter: "blur(8px) saturate(160%)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Typography variant="h4" color="text.primary">
                  {allIssues.length} compliance {allIssues.length === 1 ? "issue" : "issues"}
                </Typography>
              </Box>
              <Stack sx={{ px: { xs: 2, md: 3 }, py: 1, position: "relative", zIndex: 1 }}>
                {nonAllergenIssues.map((issue, idx) => (
                  <Box
                    key={`issue-${idx}`}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 1, sm: 2 },
                      py: 1.5,
                      minHeight: 36,
                      borderBottom: idx < nonAllergenIssues.length - 1 || allergenIssues.length > 0 ? "1px solid rgba(0,0,0,0.06)" : "none",
                      animation: `fadeInUp ${400 + idx * 40}ms ease both`,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                      {issue.blocking
                        ? <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "#991B1B", flexShrink: 0 }} />
                        : <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#92400E", flexShrink: 0 }} />
                      }
                      <Typography variant="body2" color="text.primary" sx={{ flex: 1, fontWeight: 500, minWidth: 0 }}>{issue.message}</Typography>
                    </Stack>
                    <Box sx={{ alignSelf: { xs: "flex-start", sm: "auto" }, ml: { xs: 3.5, sm: 0 }, flexShrink: 0 }}>
                      <SourceCitation source={issue.source} sourceUrl={issue.sourceUrl} sourceQuote={issue.sourceQuote} sourceQuotes={issue.sourceQuotes} highlight={issue.highlight} />
                    </Box>
                  </Box>
                ))}
                {allergenIssues.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 1, sm: 2 },
                      py: 1.5,
                      minHeight: 36,
                      animation: `fadeInUp ${400 + nonAllergenIssues.length * 40}ms ease both`,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                      <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#92400E", flexShrink: 0 }} />
                      <Typography variant="body2" color="text.primary" sx={{ flex: 1, fontWeight: 500, minWidth: 0 }}>
                        <strong>{allergenIssues.length}</strong> allergen{allergenIssues.length > 1 ? "s" : ""} require EU label declaration
                      </Typography>
                    </Stack>
                    <Box sx={{ alignSelf: { xs: "flex-start", sm: "auto" }, ml: { xs: 3.5, sm: 0 }, flexShrink: 0 }}>
                      <SourceCitation source={allergenIssues[0].source} sourceUrl={allergenIssues[0].sourceUrl} sourceQuote={allergenIssues[0].sourceQuote} />
                    </Box>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          <FormulaRiskMatrix report={riskReport} />
        </Box>
      </Collapse>
    </PageRoot>
  );
};

export default TextPhotoProcessor;
